import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";

const HiddenBetsModal = ({
  open,
  onClose,
  hiddenItems,
  onUnhide,
  onUnhideAll,
}) => {
  const groupedItems = useMemo(() => {
    const groups = {
      entire_event: [],
      market_event: [],
      market_team: [],
      specific_bet_all_bookies: [],
      specific_bet: [],
    };

    hiddenItems.forEach((item) => {
      if (groups[item.type]) {
        groups[item.type].push(item);
      }
    });

    return groups;
  }, [hiddenItems]);

  const getItemDescription = (item) => {
    const eventTime = moment(item.eventStart).format("MMM DD, YYYY HH:mm");

    switch (item.type) {
      case "entire_event":
        return {
          primary: `Entire Event: ${item.eventName}`,
          secondary: `All bets for this event • ${eventTime}`,
        };
      case "market_event":
        return {
          primary: `Market: ${item.market}`,
          secondary: `${item.eventName} • All teams • ${eventTime}`,
        };
      case "market_team":
        return {
          primary: `Market: ${item.market} for ${item.team}`,
          secondary: `${item.eventName} • ${eventTime}`,
        };
      case "specific_bet_all_bookies":
        return {
          primary: `Bet (All Bookies): ${item.bet}${
            item.player ? ` - ${item.player}` : ""
          }${item.team ? ` - ${item.team}` : ""}${
            item.line ? ` ${item.line}` : ""
          }`,
          secondary: `${item.eventName} • ${item.market} • All bookies • ${eventTime}`,
        };
      case "specific_bet":
        return {
          primary: `Specific Bet: ${item.bet}${
            item.player ? ` - ${item.player}` : ""
          }${item.team ? ` - ${item.team}` : ""}${
            item.line ? ` ${item.line}` : ""
          }`,
          secondary: `${item.eventName} • ${item.market} • ${item.bookie} • ${eventTime}`,
        };
      default:
        return {
          primary: "Unknown hide type",
          secondary: `${item.eventName} • ${eventTime}`,
        };
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "entire_event":
        return "Entire Events";
      case "market_event":
        return "Entire Markets";
      case "market_team":
        return "Team Markets";
      case "specific_bet_all_bookies":
        return "Bets (All Bookies)";
      case "specific_bet":
        return "Specific Bets";
      default:
        return "Other";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "entire_event":
        return "error";
      case "market_event":
        return "warning";
      case "market_team":
        return "info";
      case "specific_bet_all_bookies":
        return "success";
      case "specific_bet":
        return "primary";
      default:
        return "primary";
    }
  };

  const totalHiddenCount = hiddenItems.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Hidden Bets ({totalHiddenCount})</Typography>
          {totalHiddenCount > 0 && (
            <Button
              onClick={onUnhideAll}
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              color="primary"
            >
              Unhide All
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {totalHiddenCount === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No hidden bets or events
            </Typography>
          </Box>
        ) : (
          <Box>
            {Object.entries(groupedItems).map(([type, items]) => {
              if (items.length === 0) return null;

              return (
                <Box key={type} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={getTypeLabel(type)}
                      color={getTypeColor(type)}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      ({items.length} {items.length === 1 ? "item" : "items"})
                    </Typography>
                  </Box>

                  <List dense>
                    {items.map((item, index) => {
                      const description = getItemDescription(item);
                      return (
                        <ListItem
                          key={`${type}-${index}`}
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                            "&:last-child": { mb: 0 },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {description.primary}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {description.secondary}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => onUnhide(index, item)}
                              size="small"
                              color="primary"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>

                  <Divider sx={{ mt: 2 }} />
                </Box>
              );
            })}
          </Box>
        )}

        {totalHiddenCount > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Hidden items only apply to specific events
              and dates. Events older than 24 hours are automatically removed
              from this list.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HiddenBetsModal;
